import { gsap } from "gsap";
import { Graphics, Sprite, Text, Texture } from "pixi.js";

import { AbstractBitmapText } from "../../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractView } from "../../../../../abstracts/modules/AbstractView";
import { WinType } from "../../../../../abstracts/types/enums";
import { IWinLevel } from "../../../../../abstracts/types/interfaces";
import { fitDisplayObjectToWidth } from "../../../../../abstracts/utils/GraphicUtils";
import {
    fadeAndStopSound,
    playNextBackgroundMusic,
    playSound,
    stopBackgroundMusic,
} from "../../../../../abstracts/utils/SoundUtils";
import { displayConfig, winLevels } from "../../../../UCConfig";
import { UgliestCatch } from "../../../../UgliestCatch";
import { UCFontsNames, UCSoundNames } from "../../../../assetsLoader/UCAssetsNames";
import { UCFreespinIntroScreen } from "./UCFreespinIntroScreen";

export class UCFreespinOutroScreen extends UCFreespinIntroScreen {
    private totalWinText: AbstractBitmapText;
    private spinsPlayedText: Text;

    constructor(parentContainer: AbstractView, name: string = `UCFreespinOutroScreen`) {
        super(parentContainer, name);
    }

    protected override addBackgroundImage() {
        const bg: Graphics = new Graphics();
        bg.beginFill(0x000000);
        bg.drawRect(0, 0, displayConfig.backgroundWidth, displayConfig.backgroundHeight);
        bg.endFill();
        bg.alpha = 0.7;
        bg.name = `introGraphics`;
        this.addChild(bg);

        this.backgroundImage = new Sprite(Texture.from(this.getTextureName()));
        this.backgroundImage.width = 720;
        this.backgroundImage.scale.y = this.backgroundImage.scale.x;

        this.backgroundImage.position.set(480);

        this.addChild(this.backgroundImage);
    }

    protected override getScreenCustomName(): string {
        return `Freespin Outro`;
    }

    protected override getTextureName(): string {
        return `FS_OUTRO`;
    }

    protected override addFreespinsLeftText() {}

    protected continueButtonClickCallback() {
        super.continueButtonClickCallback();

        this.removeChild(this.spinsPlayedText);
        this.removeChild(this.totalWinText);
    }

    protected override init(): void {
        super.init();

        this.totalWinText = new AbstractBitmapText(`0`, {
            fontName: UCFontsNames.UC_DEFAULT,
            fontSize: 24,
        });
        this.totalWinText.name = `totalWinText`;
        this.totalWinText.position.set(this.continueButton.x + this.continueButton.width / 2, 975);

        this.spinsPlayedText = new Text(`0`, {
            fontFamily: `Arial`,
            fontSize: 28,
            fill: 0xffffff,
        });

        this.spinsPlayedText.anchor.set(0.5);
        this.spinsPlayedText.position.set(
            this.continueButton.x + this.continueButton.width / 2,
            1055
        );
    }

    public override getGsapAnimation(
        finalWinValue: number = 0,
        winLevel: IWinLevel = winLevels[0],
        shouldPlaySound: boolean = true
    ): gsap.core.Timeline {
        if (shouldPlaySound) {
            if (finalWinValue > 0) {
                playSound({ name: this.getOutroWinSoundName() });
            }
        }

        if (finalWinValue === 0) {
            playSound({ name: UCSoundNames.FS_OUTRO_BELOW_STAKE });
        }

        const spinPlayed: number = UgliestCatch.model.data.numberOfFreespinsPlayed;

        this.spinsPlayedText.text =
            UgliestCatch.apiPlugin.translations.translate(`Spins played: `) + `${spinPlayed}`;

        const tl = gsap.timeline({
            autoRemoveChildren: true,
            onComplete: () => {
                this.addChild(this.continueButton);
                this.addChild(this.spinsPlayedText);

                stopBackgroundMusic();
                playNextBackgroundMusic();
            },
        });

        this.spinsNameSprite.visible = false;
        this.spinsTextSprite.visible = false;

        const finalValueToString: string = finalWinValue === 0 ? `` : finalWinValue.toFixed(2);

        const showScreenTween = gsap.fromTo(
            this,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.3,
                onStart: () => {
                    this.totalWinText.y = 1068;
                    this.totalWinText.updateValue(``);
                    fitDisplayObjectToWidth(this.totalWinText, 700);

                    this.addChild(this.totalWinText);

                    fadeAndStopSound({ name: this.getBackgroundMusicName(), to: 0, duration: 0.5 });
                },
            }
        );

        tl.add(showScreenTween);

        if (finalWinValue > 0) {
            const counterObj = { value: 0 };

            const updateValueTween = gsap.to(counterObj, {
                value: finalValueToString,
                duration:
                    winLevel.min >= winLevels[4].min
                        ? winLevel.animationDuration / 2
                        : winLevel.animationDuration,
                onUpdate: () => {
                    this.totalWinText.updateValue(counterObj.value.toFixed(2));
                    fitDisplayObjectToWidth(this.totalWinText, 700);
                },
                onStart: () => {
                    if (shouldPlaySound) {
                        switch (winLevel.winType) {
                            case WinType.SMALL:
                                playSound({ name: UCSoundNames.WIN_SMALL });
                                break;
                            case WinType.MEDIUM:
                            case WinType.LARGE:
                            case WinType.BIG:
                                playSound({ name: UCSoundNames.WIN_COUNTUP, repeat: 100 });
                                break;

                            default:
                                playSound({ name: UCSoundNames.WIN_TINY });
                        }
                    }
                },
            });

            tl.add(updateValueTween);

            const highlightTween = gsap.to(this.totalWinText, {
                duration: 0.1,
                pixi: {
                    colorize: `white`,
                    colorizeAmount: 1,
                    brightness: 2,
                    scale: `+=0.1`,
                },
                yoyo: true,
                repeat: 1,
                repeatDelay: 0.1,
                onUpdate: () => {
                    fitDisplayObjectToWidth(this.totalWinText, 720);
                },
                onStart: () => {
                    if (shouldPlaySound) {
                        switch (winLevel.winType) {
                            case WinType.MEDIUM:
                                playSound({ name: UCSoundNames.WIN_MEDIUM });
                                break;
                            case WinType.LARGE:
                            case WinType.BIG:
                                playSound({ name: UCSoundNames.WIN_LARGE });

                                break;

                            default:
                                break;
                        }

                        fadeAndStopSound({
                            name: UCSoundNames.WIN_COUNTUP,
                            from: 1,
                            to: 0,
                            duration: 0.5,
                        });
                    }
                },
            });

            tl.add(highlightTween, `>-=0.1`);

            const moveTotalText = gsap.to(this.totalWinText, {
                duration: 0.2,
                y: 975,
            });

            tl.add(moveTotalText, `<`);
        }

        return tl;
    }

    protected getBackgroundMusicName(): string {
        return UCSoundNames.FREESPIN_MUSIC;
    }

    protected addContinueButton() {
        super.addContinueButton();

        this.continueButton.y = 1076;
    }

    protected getOutroWinSoundName(): string {
        return UCSoundNames.FS_OUTRO_WIN;
    }
}
